import type { JSX } from 'react';
import { isRouteErrorResponse, useRouteError } from 'react-router';

import { isAppError } from '@/shared/api';
import { adminPath, isAdminHost } from '@/shared/config';
import { type ErrorKind, ErrorPage } from '@/shared/ui';

import { isStaleBuildError } from './staleBuild';

/**
 * What a route renders when it throws — a lazy chunk that will not load, a page
 * that crashed, a loader that rejected, an address that does not exist.
 *
 * Without it React Router falls back to its own development screen, which
 * prints the raw exception and a paragraph addressed to «Hey developer» over
 * the patient's booking page.
 */
function kindOf(error: unknown): ErrorKind {
  if (isStaleBuildError(error)) {
    return 'staleBuild';
  }

  // A `throw new Response(…)` from the router itself: today only the 404 the
  // catch-all raises, but a loader may add others.
  if (isRouteErrorResponse(error)) {
    return error.status === 404 ? 'notFound' : error.status === 403 ? 'forbidden' : 'crash';
  }

  if (isAppError(error)) {
    if (error.status === 0) {
      return 'offline';
    }

    return error.status === 404 ? 'notFound' : error.status === 403 ? 'forbidden' : 'crash';
  }

  return 'crash';
}

export function RouteErrorBoundary(): JSX.Element {
  const error = useRouteError();
  const kind = kindOf(error);

  return (
    <ErrorPage
      homeHref={isAdminHost() ? adminPath() : '/'}
      kind={kind}
      // A wrong address does not become right by being asked for twice; every
      // other kind here is worth one more attempt.
      onRetry={
        kind === 'notFound'
          ? undefined
          : (): void => {
              window.location.reload();
            }
      }
      reference={isAppError(error) ? error.correlationId : undefined}
    />
  );
}
